/*
 * Module for handling selecting a snippet to add to a response
 */

define([
  "backbone",
  "marionette",
  "messaging/messenger",
  "modules/shared/snippetSearch/snippetSearchModel",
  "modules/shared/snippetSearch/snippetSearchView",
  "modules/shared/navTree/navTreeModule",
  "modules/shared/selectableList/selectableListModule",
  "modules/topicsSplit/snippet/snippetModel",
], function (
  Backbone,
  Marionette,
  Messenger,
  SnippetSearchModel,
  SnippetSearchView,
  NavTreeModule,
  SelectableListModule,
  SnippetModel,
) {
  "use strict";

  return Marionette.Controller.extend({
    initialize: function (options) {
      this.name = "snippetSearch";
      this.namespace = options.namespace;
      this.region = options.region;
      this.selectedSnippet = null;
      this.rootSnippetGroup = Messenger.reqres.request("root:snippet:group");
      this.selectedGroup = this.rootSnippetGroup;
      this._setUpVents();
    },
    show: function ({ languageId, snippetIdToBlock, touchpointId }) {
      this.selectedSnippet = new SnippetModel.snippet();
      this.selectedSnippet.snippetIdToBlock = snippetIdToBlock;
      this.selectedSnippet.languageId = languageId;
      this.selectedSnippet.touchpointId = touchpointId;
      const selectedSnippet = this.selectedSnippet;
      this.layout = new SnippetSearchView.snippetSearchLayout({ model: selectedSnippet });

      this.tree = this.addSubModule(
        new NavTreeModule({
          region: this.layout.snippetTree,
          nameAttribute: "name",
          namespace: this.namespace,
        }),
      );
      this.list = this.addSubModule(
        new SelectableListModule({
          namespace: this.namespace,
          region: this.layout.snippetList,
        }),
      );

      //user selected a typeahead search item, so get the content (should be only one) from the server and
      //then select the matching content
      this.layout.on("search", (matchedModel) => {
        if (this._filterSnippet(matchedModel)) {
          const snippetSearchModel = new SnippetSearchModel.snippetSearch();
          snippetSearchModel.set("snippetId", matchedModel.id);
          snippetSearchModel.fetch();
          snippetSearchModel.onSynced(() => {
            this.selectedSnippet.set(snippetSearchModel.attributes);
            this._showSubViews(this.selectedGroup);
            Messenger.vent.trigger(
              Messenger.getFullName("snippet:search:selected", this.namespace),
              snippetSearchModel,
            );
          });
        }
      });

      this.layout.on("show", () => {
        this._showSubViews();
      });
      this.showRegion(this.layout);
    },
    focus: function () {
      if (this.isOpen && this.list) {
        this.list.focus();
      }
    },
    setSelected: function (snippet) {
      this.selectedSnippet.set(snippet.attributes);
      this._showSubViews(this.selectedGroup);
    },
    _showSubViews: function (selectedGroup) {
      let group;
      // select and display the previously selected group if available, otherwise select root folder
      if (!selectedGroup) {
        group = this.rootSnippetGroup;
      } else {
        group = selectedGroup;
      }
      this.tree.show(this.rootSnippetGroup);
      group.trigger("select");
      this._filterSnippets(group).then((snippets) => {
        this.list.show(snippets);
      });
    },
    _setUpVents: function () {
      Messenger.vent.on(Messenger.getFullName("select:group", this.namespace), (groupView) => {
        this.selectedGroup = groupView.model;
        this._filterSnippets(groupView.model).then((snippets) => {
          this.list.show(snippets);
        });
      });
    },
    _filterSnippet: function (snippet) {
      const languageId = this.selectedSnippet.languageId;
      const snippetIdToBlock = this.selectedSnippet.snippetIdToBlock;
      const touchpointId = this.selectedSnippet.touchpointId;
      return (
        snippet.id != snippetIdToBlock &&
        (!snippet.get("languageId") || !languageId || snippet.get("languageId") == languageId) &&
        (!snippet.get("touchpointId") ||
          !touchpointId ||
          snippet.get("touchpointId") == touchpointId)
      );
    },
    /**
     * Filters the snippets in a particular group to only show the ones that match the filter
     * for this snippet search. It will filter out a particular snippet that matches a particular
     * ID to avoid a snippet from including itself. It will filter out any snippets that are
     * not for the language of the response or snippet. It will filter out any snippets that are
     * not for the touchpoint of the response or snippet.
     *
     * @param group The parent group for the snippets
     *
     * @return {Promise<Backbone.Collection>} A collection containing the snippets that match filter
     * @private
     */
    _filterSnippets: async function (group) {
      const snippets = group.snippets();
      await snippets.onSyncedPromise();
      if (
        !this.selectedSnippet.snippetIdToBlock &&
        !this.selectedSnippet.languageId &&
        !this.selectedSnippet.touchpointId
      ) {
        return snippets;
      }
      return new Backbone.Collection(snippets.filter(this._filterSnippet.bind(this)));
    },
  });
});
